import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  clearTokenFromHash,
  getStoredToken,
  readTokenFromHash,
  upsertStoredGroup,
} from '../lib/storage';

/**
 * このグループのトークンを解決する（§4.1 G-03）。
 *
 * 共有URLから開いた初回はフラグメントに載っている。保存してしまえば2回目以降は
 * localStorage 側が使われる。
 *
 * 読み取りは副作用を持たず、保存は同じ値の上書きで冪等なので、StrictMode が
 * 初期化関数を2回呼んでも結果は変わらない。
 */
function resolveToken(groupId: string): string | null {
  const fromHash = readTokenFromHash();
  if (fromHash) {
    upsertStoredGroup({ groupId, token: fromHash });
    return fromHash;
  }
  return getStoredToken(groupId);
}

export function useGroupToken(groupId: string | undefined): string | null {
  const [token] = useState<string | null>(() => (groupId ? resolveToken(groupId) : null));

  // 保存が済んでから URL を掃除する
  useEffect(() => {
    if (token && readTokenFromHash()) clearTokenFromHash();
  }, [token]);

  return token;
}

/** グループ情報＋メンバー＋ルールの一括取得 */
export function useGroup(groupId: string | undefined, token: string | null) {
  return useQuery({
    queryKey: ['group', groupId],
    queryFn: async ({ signal }) => {
      const bundle = await api.getGroup(groupId!, token!, signal);
      // 一覧カードの表示用キャッシュを最新化しておく
      upsertStoredGroup({
        groupId: bundle.group.groupId,
        token: token!,
        name: bundle.group.name,
        matchCount: bundle.group.matchCount,
        playerCount: bundle.rules.find((r) => r.isDefault)?.playerCount,
      });
      return bundle;
    },
    enabled: Boolean(groupId && token),
  });
}

/** 対局一覧。サーバー側はフェーズ4で実装するため、当面は 501 が返る */
export function useMatches(groupId: string | undefined, token: string | null, limit = 20) {
  return useQuery({
    queryKey: ['matches', groupId, limit],
    queryFn: ({ signal }) => api.listMatches(groupId!, token!, { limit }, signal),
    enabled: Boolean(groupId && token),
  });
}
